import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { ImageInsTargetRequest } from "./imageInsSlice";

export interface ImageUploadState {
  filename: string;
  preview: string;
  state: LoadStateInfo;
}

export interface ImagesUploadState {
  images: Record<string, ImageUploadState>;
}

const initialState: ImagesUploadState = {
  images: {},
};

export interface ImageUploadRequest {
  image: ImageUploadState;
  file: File;
  target: ImageInsTargetRequest;
}

export const imageUpload = createAsyncThunk(
  "imageUpload",
  async (param: ImageUploadRequest, thunkAPI) => {
    console.debug(`upload image ${param.image.filename}`);

    let queryParams = `filename=${param.file.name}`;
    queryParams += `&schoolId=${param.target.schoolId}`;
    queryParams += `&avatar=${param.target.avatar}`;
    if(param.target.conversationId)
        queryParams += `&conversationId=${param.target.conversationId}`;
    if(param.target.partyId)
        queryParams += `&partyId=${param.target.partyId}`;
    if(param.target.objectTarget)
        queryParams += `&objectTarget=${param.target.objectTarget}`;
    if(param.target.targetId)
        queryParams += `&targetId=${param.target.targetId}`;

    const response = await fetch(`${getBackendUrl()}/api/image?${queryParams}`, {
      method: "POST",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": `${param.file.type}`,
      },
      body: param.file
    });

    if(response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse( response ));
    }
  }
);


export const imagesUploadSlice = createSlice({
  name: "imagesUploadSlice",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    add: (state, action: PayloadAction<{filename: string, preview: string}>) => {
      if(!state.images[action.payload.filename]) {
        state.images[action.payload.filename] = {
          filename: action.payload.filename,
          preview: action.payload.preview, 
          state: notStarted()
        };
      }
    },
    retry: (state, action: PayloadAction<string>) => {
      state.images[action.payload].state = notStarted();
    },
    remove: (state, action: PayloadAction<string>) => {
      delete state.images[action.payload];
    },
    clean: (state) => {
      state.images = {};
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(imageUpload.pending, (state, action) => {
        state.images[action.meta.arg.image.filename].state =  loading();
      })
      .addCase(imageUpload.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.images[action.meta.arg.image.filename].state =  loaded();
      })
      .addCase(imageUpload.rejected, (state, action) => {
        state.images[action.meta.arg.image.filename].state = error(action);
      });
    },
});

export const { } = imagesUploadSlice.actions;

export const selectImagesUpload = (state: RootState) => state.imagesUpload;

export default imagesUploadSlice.reducer;
